import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ComponentDialog from "@/components/ComponentDialog";
import TypeDialog from "@/components/TypeDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import {
  Package,
  Plus,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  ArrowLeft,
  ImageIcon,
  ImageOff,
  GripVertical,
  FileText,
  Copy,
} from "lucide-react";

function singular(word) {
  const w = (word || "").trim();
  if (!w) return "item";
  if (/ies$/i.test(w)) return w.slice(0, -3) + "y";
  if (/(ses|xes|zes|ches|shes)$/i.test(w)) return w.slice(0, -2);
  if (/ss$/i.test(w)) return w;
  if (/s$/i.test(w)) return w.slice(0, -1);
  return w;
}

function Thumb({ src, className, iconSize = "h-8 w-8" }) {
  const [err, setErr] = useState(false);
  if (!src || err)
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className}`}>
        <ImageIcon className={iconSize} strokeWidth={1.25} />
      </div>
    );
  return <img src={resolveImageUrl(src)} alt="" loading="lazy" decoding="async" className={`object-cover ${className}`} onError={() => setErr(true)} />;
}

export default function Components() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [compDialog, setCompDialog] = useState({ open: false, initial: null });
  const [typeDialog, setTypeDialog] = useState({ open: false, component: null, initial: null });
  const [confirm, setConfirm] = useState(null); // {type:'component'|'type', component, typeObj}
  const [dragIndex, setDragIndex] = useState(null);
  const [materialFilter, setMaterialFilter] = useState("all");
  const [typeDragIndex, setTypeDragIndex] = useState(null);
  const [avgCosts, setAvgCosts] = useState({});
  const [coPackers, setCoPackers] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("all");
  useEffect(() => { api.get("/avg-costs").then((r) => setAvgCosts(r.data?.by_component || {})).catch(() => {}); }, []);
  useEffect(() => { api.get("/co-packers").then((r) => setCoPackers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { setSourceFilter("all"); }, [selectedId]);
  const sourceOptions = [
    { id: "waterco", name: "WaterCo Springs" },
    ...coPackers.filter((cp) => (cp.status || "active") === "active").map((cp) => ({ id: cp.id, name: cp.company_name })),
  ];
  const srcNames = Object.fromEntries(sourceOptions.map((s) => [s.id, s.name]));

  const persistTypeOrder = async (componentId, ids) => {
    try {
      await api.put(`/components/${componentId}/types/reorder`, { ids });
    } catch (e) {
      toast.error("Failed to save order");
      load();
    }
  };

  const onDropType = (to) => {
    setComponents((prev) => prev.map((c) => {
      if (c.id !== selectedId) return c;
      if (typeDragIndex == null || typeDragIndex === to) return c;
      const next = [...(c.types || [])];
      const [moved] = next.splice(typeDragIndex, 1);
      next.splice(to, 0, moved);
      persistTypeOrder(c.id, next.map((t) => t.id));
      return { ...c, types: next };
    }));
    setTypeDragIndex(null);
  };

  const persistOrder = async (ids) => {
    try {
      await api.put("/components/reorder", { ids });
    } catch (e) {
      toast.error("Failed to save order");
      load();
    }
  };

  const onDropCard = (to) => {
    setComponents((prev) => {
      if (dragIndex == null || dragIndex === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(to, 0, moved);
      persistOrder(next.map((c) => c.id));
      return next;
    });
    setDragIndex(null);
  };

  const setMaterial = async (c, materials) => {
    try {
      await api.put(`/components/${c.id}/material`, { materials });
      toast.success(materials.length ? `${c.name} marked as ${materials.join(" + ")}` : `${c.name} material cleared`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update material");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/components");
      setComponents(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load components");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = components.find((c) => c.id === selectedId) || null;

  const submitComponent = async (payload) => {
    try {
      if (compDialog.initial) {
        await api.put(`/components/${compDialog.initial.id}`, payload);
        toast.success("Component updated");
      } else {
        await api.post("/components", payload);
        toast.success("Component created");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save component");
      throw e;
    }
  };

  const submitType = async (payload) => {
    const { component, initial } = typeDialog;
    try {
      if (initial) {
        await api.put(`/components/${component.id}/types/${initial.id}`, payload);
        toast.success("Type updated");
      } else {
        await api.post(`/components/${component.id}/types`, payload);
        toast.success("Type added");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save type");
      throw e;
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "component") {
        await api.delete(`/components/${confirm.component.id}`);
        toast.success("Component deleted");
        if (selectedId === confirm.component.id) setSelectedId(null);
      } else {
        await api.delete(`/components/${confirm.component.id}/types/${confirm.typeObj.id}`);
        toast.success("Type deleted");
      }
      await load();
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setConfirm(null);
    }
  };

  // ---------------------------------------------------------------- Detail view
  if (selected) {
    const addWord = singular(selected.name);
    const hasDesigns = (selected.types || []).some((t) => (t.designs || []).length > 0);
    const designMatches = (d) => sourceFilter === "all" || (d.sources || []).includes(sourceFilter);
    const visibleTypes = (selected.types || []).filter((t) => sourceFilter === "all" || (t.designs || []).some(designMatches));
    return (
      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="component-detail">
        <button onClick={() => setSelectedId(null)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-components">
          <ArrowLeft className="h-4 w-4" /> All components
        </button>

        <div className="flex flex-col gap-6 border border-slate-200 p-5 sm:flex-row sm:items-start">
          <Thumb src={selected.image_url} className="h-40 w-40 shrink-0 border border-slate-200" iconSize="h-12 w-12" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900">{selected.name}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {selected.variation_label?.trim() && (
                    <span className="inline-block bg-[#002FA7]/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.1em] text-[#002FA7]">
                      Varies by {selected.variation_label}
                    </span>
                  )}
                  <MaterialChips c={selected} setMaterial={setMaterial} />
                </div>
                <AvgCostLine c={selected} cost={avgCosts[selected.id]} />
              </div>
              <Button variant="outline" className="rounded-sm" onClick={() => setCompDialog({ open: true, initial: selected })} data-testid="edit-component-detail">
                <Pencil className="mr-1 h-4 w-4" /> Edit
              </Button>
            </div>
            {selected.description?.trim() && <p className="mt-3 text-sm text-slate-600">{selected.description}</p>}
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">
              {selected.name} <span className="text-sm font-normal text-slate-400">({selected.types?.length || 0})</span>
            </h2>
            {hasDesigns && (
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="h-8 w-52 rounded-sm text-xs" data-testid="design-source-filter">
                  <span className={sourceFilter === "all" ? "text-slate-500" : "font-semibold text-[#002FA7]"}>
                    {sourceFilter === "all" ? "All Sources" : srcNames[sourceFilter] || "Source"}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sourceOptions.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={() => setTypeDialog({ open: true, component: selected, initial: null })} data-testid="add-type-button">
            <Plus className="mr-1 h-4 w-4" /> Add {addWord}
          </Button>
        </div>

        {(!selected.types || selected.types.length === 0) ? (
          <div className="mt-4 flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-20 text-center" data-testid="types-empty-state">
            <ImageOff className="h-9 w-9 text-slate-300" strokeWidth={1.5} />
            <p className="mt-3 max-w-sm text-sm text-slate-500">No {selected.name.toLowerCase()} added yet. Add one with its own image and specifications.</p>
          </div>
        ) : visibleTypes.length === 0 ? (
          <div className="mt-4 flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-20 text-center" data-testid="source-filter-empty">
            <ImageOff className="h-9 w-9 text-slate-300" strokeWidth={1.5} />
            <p className="mt-3 max-w-sm text-sm text-slate-500">No designs sourced from {srcNames[sourceFilter] || "this source"} yet. Edit a design and check its sources.</p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleTypes.map((t) => {
              const idx = selected.types.indexOf(t);
              const shownDesigns = (t.designs || []).filter(designMatches);
              return (
              <div
                key={t.id}
                draggable={sourceFilter === "all"}
                onDragStart={() => setTypeDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropType(idx)}
                onDragEnd={() => setTypeDragIndex(null)}
                className={`group flex flex-col border bg-white transition-colors hover:border-slate-300 ${typeDragIndex === idx ? "border-[#002FA7] opacity-50" : "border-slate-200"}`}
                data-testid={`type-card-${t.id}`}
              >
                <Thumb src={t.image_url} className="aspect-[4/3] w-full border-b border-slate-200" iconSize="h-10 w-10" />
                <div className="flex items-start justify-between gap-2 p-4">
                  <div className="flex min-w-0 items-start gap-2">
                    <span className="mt-0.5 shrink-0 cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing" title="Drag to reorder" data-testid={`type-drag-handle-${t.id}`}>
                      <GripVertical className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-base font-bold tracking-tight text-slate-900">{t.name}</h3>
                      {t.attributes?.length > 0 && (
                        <dl className="mt-2 space-y-1">
                          {t.attributes.map((a, ai) => (
                            <div key={`${a.label}-${ai}`} className="flex gap-2 text-xs">
                              <dt className="text-slate-400">{a.label}:</dt>
                              <dd className="font-medium text-slate-700">{a.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                      {t.tech_data_sheet_url && (
                        <a href={resolveImageUrl(t.tech_data_sheet_url)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline" data-testid={`type-datasheet-${t.id}`}>
                          <FileText className="h-3.5 w-3.5" /> Data sheet
                        </a>
                      )}
                      {(t.units_per_case || t.cases_per_pallet || t.units_per_pallet) && (
                        <p className="mt-1.5 text-[11px] text-slate-500" data-testid={`type-packaging-${t.id}`}>
                          {t.units_per_case ? `${Number(t.units_per_case).toLocaleString()}/case` : "No cases"}
                          {t.cases_per_pallet ? ` · ${Number(t.cases_per_pallet).toLocaleString()} cases/pallet` : ""}
                          {t.units_per_pallet ? ` · ${Number(t.units_per_pallet).toLocaleString()}/pallet` : ""}
                        </p>
                      )}
                      {shownDesigns.length > 0 && (
                        <div className="mt-2 border-t border-slate-100 pt-1.5" data-testid={`type-designs-${t.id}`}>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Designs ({shownDesigns.length})</p>
                          <div className="mt-1 max-h-56 space-y-0.5 overflow-y-auto pr-1">
                            {shownDesigns.map((d) => (
                              <div key={d.id} className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`design-line-${d.id}`}>
                                <span className="truncate font-medium text-slate-700">{d.name}</span>
                                {d.ball_code && (
                                  <button type="button" title="Copy BALL code"
                                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(d.ball_code).then(() => toast.success(`BALL code ${d.ball_code} copied`)).catch(() => toast.error("Couldn't copy — your browser blocked clipboard access")); }}
                                    className="inline-flex items-center gap-1 rounded-sm bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white hover:bg-slate-600"
                                    data-testid={`design-ball-copy-${d.id}`}>
                                    {d.ball_code} <Copy className="h-2.5 w-2.5" />
                                  </button>
                                )}
                                {(d.sources || []).map((sid) => srcNames[sid] ? (
                                  <span key={sid} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500" data-testid={`design-source-badge-${d.id}-${sid}`}>{srcNames[sid]}</span>
                                ) : null)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-[#002FA7]" onClick={() => setTypeDialog({ open: true, component: selected, initial: t })} data-testid={`edit-type-${t.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => setConfirm({ type: "type", component: selected, typeObj: t })} data-testid={`delete-type-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        <ComponentDialog open={compDialog.open} onOpenChange={(o) => setCompDialog((p) => ({ ...p, open: o }))} initial={compDialog.initial} title="Edit Component" onSubmit={submitComponent} />
        <TypeDialog open={typeDialog.open} onOpenChange={(o) => setTypeDialog((p) => ({ ...p, open: o }))} initial={typeDialog.initial} components={components} component={selected} variationLabel={selected.variation_label} itemLabel={addWord} isLabel={selected?.has_designs ?? /label/i.test(selected?.name || "")} title={typeDialog.initial ? `Edit ${addWord}` : `Add ${addWord}`} onSubmit={submitType} />
        <DeleteConfirm confirm={confirm} setConfirm={setConfirm} doDelete={doDelete} />
      </main>
    );
  }

  // ---------------------------------------------------------------- List view
  return (
    <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="components-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Raw Material Catalog</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Components</h1>
          <p className="mt-2 text-sm text-slate-500">{components.length} components</p>
        </div>
        <Button onClick={() => setCompDialog({ open: true, initial: null })} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-component-button">
          <Plus className="mr-1 h-4 w-4" /> New Component
        </Button>
      </div>

      <div className="mt-10">
        <div className="mb-4 inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="components-material-filter">
          {[["all", "All"], ["PET", "PET"], ["ALUM", "ALUM"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setMaterialFilter(v)}
              className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${materialFilter === v ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`components-material-${v.toLowerCase()}`}>
              {lbl}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" /></div>
        ) : components.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-24 text-center" data-testid="empty-state">
            <Package className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
            <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">No components yet</h2>
            <p className="mt-2 max-w-sm text-sm text-slate-500">Create your first component like Preforms or Caps, then add its variations.</p>
            <Button onClick={() => setCompDialog({ open: true, initial: null })} className="mt-6 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="empty-new-component-button">
              <Plus className="mr-1 h-4 w-4" /> New Component
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {components.filter((c) => materialFilter === "all" || matsOf(c).includes(materialFilter)).map((c) => {
              const idx = components.indexOf(c);
              return (
              <div
                key={c.id}
                draggable={materialFilter === "all"}
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropCard(idx)}
                onDragEnd={() => setDragIndex(null)}
                className={`group flex flex-col border bg-white text-left transition-colors hover:border-[#002FA7] ${dragIndex === idx ? "border-[#002FA7] opacity-50" : "border-slate-200"}`}
                data-testid={`component-card-${c.id}`}
              >
                <button onClick={() => setSelectedId(c.id)} className="block w-full text-left" data-testid={`open-component-${c.id}`}>
                  <Thumb src={c.image_url} className="aspect-[4/3] w-full border-b border-slate-200" iconSize="h-10 w-10" />
                </button>
                <div className="flex items-start justify-between gap-2 p-4">
                  <div className="flex min-w-0 items-start gap-2">
                    <span className="mt-0.5 shrink-0 cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing" title="Drag to reorder" data-testid={`drag-handle-${c.id}`}>
                      <GripVertical className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <button onClick={() => setSelectedId(c.id)} className="min-w-0 text-left">
                        <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 truncate">{c.name}</h3>
                        <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-400">
                          {c.types?.length || 0} {((c.types?.length || 0) === 1 ? singular(c.name) : c.name).toLowerCase()}
                        </p>
                      </button>
                      <MaterialChips c={c} setMaterial={setMaterial} />
                      <AvgCostLine c={c} cost={avgCosts[c.id]} />
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-slate-500" data-testid={`component-menu-${c.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem onClick={() => setSelectedId(c.id)} data-testid={`view-component-${c.id}`}>Open</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setCompDialog({ open: true, initial: c })} data-testid={`edit-component-${c.id}`}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => setConfirm({ type: "component", component: c })} data-testid={`delete-component-${c.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <ComponentDialog open={compDialog.open} onOpenChange={(o) => setCompDialog((p) => ({ ...p, open: o }))} initial={compDialog.initial} title={compDialog.initial ? "Edit Component" : "New Component"} onSubmit={submitComponent} />
      <DeleteConfirm confirm={confirm} setConfirm={setConfirm} doDelete={doDelete} />
    </main>
  );
}

export const matsOf = (c) => c.materials || (c.material ? [c.material] : []);

function AvgCostLine({ c, cost }) {
  if (!cost?.avg_unit_cost) return null;
  const fmt$ = (v, d = 2) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
          className="mt-1.5 inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-emerald-700 hover:bg-emerald-100"
          data-testid={`avg-cost-${c.id}`} title="Average per-unit cost from supplier invoices — click for breakdown">
          Avg ${cost.avg_unit_cost.toFixed(4)}/unit
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 rounded-sm p-3 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`avg-cost-popover-${c.id}`}>
        <p className="font-heading text-sm font-bold text-slate-900">{c.name} — Avg Cost</p>
        <dl className="mt-2 space-y-1 text-slate-600">
          <div className="flex justify-between"><dt>Average per unit</dt><dd className="font-semibold text-emerald-700">${cost.avg_unit_cost.toFixed(4)}</dd></div>
          <div className="flex justify-between"><dt>Last price paid</dt><dd className={`font-semibold ${cost.last_unit_cost > cost.avg_unit_cost ? "text-amber-600" : "text-slate-800"}`}>${Number(cost.last_unit_cost || 0).toFixed(4)}{cost.last_date ? ` (${cost.last_date})` : ""}</dd></div>
          <div className="flex justify-between"><dt>Invoiced purchases</dt><dd className="font-semibold text-slate-800">{cost.purchases}</dd></div>
          <div className="flex justify-between"><dt>Total units</dt><dd className="font-semibold text-slate-800">{Number(cost.total_units).toLocaleString()}</dd></div>
          <div className="flex justify-between"><dt>Total spend</dt><dd className="font-semibold text-slate-800">{fmt$(cost.total_spend)}</dd></div>
        </dl>
        <p className="mt-2 text-[10px] text-slate-400">Lifetime weighted average = total dollars invoiced ÷ total units purchased.</p>
      </PopoverContent>
    </Popover>
  );
}

function MaterialChips({ c, setMaterial }) {
  const mats = matsOf(c);
  return (
    <div className="mt-1.5 flex items-center gap-1">
      {["PET", "ALUM"].map((m) => (
        <button
          key={m}
          type="button"
          onClick={(e) => { e.stopPropagation(); setMaterial(c, mats.includes(m) ? mats.filter((x) => x !== m) : [...mats, m]); }}
          title={mats.includes(m) ? `Clear ${m}` : `Mark ${c.name} as ${m}`}
          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-[0.08em] transition-colors ${
            mats.includes(m)
              ? m === "PET"
                ? "border-[#002FA7] bg-[#002FA7] text-white"
                : "border-slate-700 bg-slate-700 text-white"
              : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"
          }`}
          data-testid={`component-material-${m.toLowerCase()}-${c.id}`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function DeleteConfirm({ confirm, setConfirm, doDelete }) {
  return (
    <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
      <AlertDialogContent className="rounded-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading tracking-tight">
            {confirm?.type === "component" ? `Delete "${confirm?.component?.name}"?` : `Delete "${confirm?.typeObj?.name}"?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirm?.type === "component"
              ? "This permanently removes the component and all of its types."
              : "This permanently removes this type."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete">Cancel</AlertDialogCancel>
          <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
